import { useLayoutEffect } from 'react';

import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRefundEligibility } from 'hooks/refund-eligibility/use-refund-eligibility';
import { routes } from 'utils/routing';

export interface IUseRedirectToSupportFormIfRefundIneligibleInput {
  orderId: string;
  categoryId: string;
  subcategoryId: string;
}

/**
 * This hook redirects the customer to the support flow if the order passed in is not eligible for a refund
 */
export const useRedirectToSupportFormIfRefundIneligible = ({
  orderId,
  categoryId,
  subcategoryId,
}: IUseRedirectToSupportFormIfRefundIneligibleInput) => {
  const {
    isDeterminingRefundEligibility,
    isOrderEligibleForRefund,
    refundIneligibilityReason,
  } = useRefundEligibility({ orderId });
  const { navigate } = useNavigation();

  /**
   * useLayoutEffect can prevent the component consuming this hook from rendering before redirecting,
   * eliminating a distracting flash of unintended content from showing
   */
  useLayoutEffect(() => {
    if (isDeterminingRefundEligibility) {
      return;
    }

    if (!isOrderEligibleForRefund && categoryId && subcategoryId) {
      const newQuery = new URLSearchParams({
        orderId,
        categoryId,
        subcategoryId,
      }).toString();
      // multistep support form allows including an orderId with submission
      const url = `${routes.supportForm}?${newQuery}`;

      navigate(url, {
        state: { refundIneligibilityReason },
      });
    }
  }, [
    categoryId,
    isDeterminingRefundEligibility,
    isOrderEligibleForRefund,
    navigate,
    orderId,
    refundIneligibilityReason,
    subcategoryId,
  ]);

  return { isDeterminingRefundEligibility };
};
